import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Table, Badge, Modal } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import TournamentHeader from '../../Common/TournamentHeader';
import ShimmerLoader from '../../Common/ShimmerLoader';
import LogoUpload from '../../components/LogoUpload/LogoUpload';
import SeasonHeroImageUpload from '../../components/SeasonHeroImageUpload/SeasonHeroImageUpload';
import { DeleteModal } from '../../Common/DeleteModal';
import { 
  getTournamentById, 
  ManageTournament, 
  formatDate, 
  getTournamentLogoUrl,
  getTournamentSeasons,
  getAllTournamentSeasons,
  TournamentSeason,
  getAllTournamentsForAdmin,
  addTournamentSeason,
  editTournamentSeason,
  deleteTournamentSeason,
} from '../../api/tournament/manageTournamentService';
import { editTournament, TournamentPayload } from '../../api/tournament/tournamentService';
import { hasAnyPermission } from '../../utils/roleBasedAccess';
import { SEASON_VERTICAL_MEDIA_ENABLED } from '../../constants/featureFlags';
import { useSelector } from 'react-redux';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';
import {
  buildTvPassConfig,
  SEASON_LOCATION_ERROR_MESSAGE,
  SEASON_LOCATION_INPUT_PATTERN,
  SEASON_LOCATION_PATTERN,
  validateSeasonDates,
} from './seasonSetup';

const SeasonsListingPage: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);

  // State management
  const [tournament, setTournament] = useState<ManageTournament | null>(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(tournamentId || null);
  const [tournaments, setTournaments] = useState<ManageTournament[]>([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  const [seasons, setSeasons] = useState<TournamentSeason[]>([]);
  const [loading, setLoading] = useState(true);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  
  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [inlineEditData, setInlineEditData] = useState<TournamentPayload>({
    name: '',
    shortName: '',
    logo: '',
    heroImage: '',
    location: '',
    startDate: '',
    endDate: '',
    status: '',
    sports: '',
    sortNo: 1
  });
  const [editLoading, setEditLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  // Delete season modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSeasonTarget, setDeleteSeasonTarget] = useState<TournamentSeason | null>(null);
  const [isDeletingSeason, setIsDeletingSeason] = useState(false);
  const [deleteSeasonError, setDeleteSeasonError] = useState<string | null>(null);

  // Season inline form state
  const [isEditingSeason, setIsEditingSeason] = useState(false);
  const [editingSeason, setEditingSeason] = useState<TournamentSeason | null>(null);
  const [hasDefaultSeason, setHasDefaultSeason] = useState(false);
  const [seasonFormData, setSeasonFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    seasonYearRange: '',
    isFeaturedOnTv: false,
    showOnHomePage: true,
    tvSortOrder: '2',
    tvPreviewDuration: '',
    sortOrder: '2',
    tvAccessType: 'paid',
    recordingAccessType: 'free',
    highlightAccessType: 'free',
    matchPassEnabled: false,
    matchPassPrice: '',
    seasonPassEnabled: false,
    seasonPassPrice: '',
    tournamentPassEnabled: false,
    heroImage: null as string | null,
    detailHeroImage: null as string | null,
    detailHeroImageApp: null as string | null,
    matchCardBackgroundImage: null as string | null,
  });
  const [seasonMediaTouched, setSeasonMediaTouched] = useState({
    heroImage: false,
    detailHeroImage: false,
    detailHeroImageApp: false,
    matchCardBackgroundImage: false,
  });
  const [seasonMediaUploading, setSeasonMediaUploading] = useState({
    heroImage: false,
    detailHeroImage: false,
    detailHeroImageApp: false,
    matchCardBackgroundImage: false,
  });
  const isSeasonMediaUploading = Object.values(seasonMediaUploading).some(Boolean);
  const [seasonFormErrors, setSeasonFormErrors] = useState<{[key: string]: string}>({});
  const [seasonFormLoading, setSeasonFormLoading] = useState(false);

  // Refs to prevent duplicate API calls
  const isLoadingSeasonsRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const lastLoadedTournamentIdRef = useRef<string | null>(null);

  // Check if user has access
  const userData = {
    ...user,
    subRole: role,
    permissions: permissions,
  };

  const canAccessFixtures = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.tournaments.view',
    'tournament.tournaments.manage',
    'tournament.fixtures.manage',
  ]);

  // Load all tournaments for dropdown
  // OPTIMIZATION: First check if tournaments are available from location state (passed from parent page)
  // Only make API call if tournaments are not available from location state
  const loadAllTournaments = async () => {
    // Check if tournaments are available from location state (passed from parent page)
    const tournamentsFromState = (location.state as any)?.allTournaments;
    
    if (tournamentsFromState && Array.isArray(tournamentsFromState) && tournamentsFromState.length > 0) {
      // Use tournaments from location state - no API call needed
      setTournaments(tournamentsFromState);
      setTournamentsLoading(false);
      return;
    }
    
    // Only make API call if tournaments are not available from location state
    try {
      setTournamentsLoading(true);
      const response = await getAllTournamentsForAdmin(1, 1000, '', '', '');
      setTournaments(response.tournaments || []);
    } catch (error: any) {
      console.error('Error loading tournaments:', error);
    } finally {
      setTournamentsLoading(false);
    }
  };

  // Load tournament details
  const loadTournamentDetails = async (id: string) => {
    if (!id) {
      setTournament(null);
      return;
    }

    try {
      setLoading(true);
      // Get sport from location state (selectedTournament) or URL search params
      // Sport is REQUIRED for tournament management module
      const selectedTournamentFromState = (location.state as any)?.selectedTournament;
      const sportFromState = selectedTournamentFromState?.sports;
      const urlParams = new URLSearchParams(location.search);
      const sportFromUrl = urlParams.get('sport');
      const sport = sportFromState || sportFromUrl;
      
      if (!sport) {
        throw new Error('Sport parameter is required. Please navigate from tournament list or include ?sport=Football in URL.');
      }
      
      const tournamentData = await getTournamentById(id, sport);
      setTournament(tournamentData);
    } catch (error: any) {
      console.error('Error loading tournament details:', error);
      setTournament(null);
    } finally {
      setLoading(false);
    }
  };

  // Sort seasons: ongoing first, then completed, then others
  const sortSeasonsByStatus = (seasonsList: TournamentSeason[]): TournamentSeason[] => {
    const statusOrder: { [key: string]: number } = {
      'ongoing': 1,
      'completed': 2,
      'upcoming': 3,
      'cancelled': 4
    };

    return [...seasonsList].sort((a, b) => {
      if (a.seasonType === 'DEFAULT' && b.seasonType !== 'DEFAULT') {
        return -1;
      }
      if (b.seasonType === 'DEFAULT' && a.seasonType !== 'DEFAULT') {
        return 1;
      }

      const statusA = (a.status || '').toLowerCase();
      const statusB = (b.status || '').toLowerCase();
      const orderA = statusOrder[statusA] || 99;
      const orderB = statusOrder[statusB] || 99;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same status, sort by start date (newest first)
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dateB - dateA;
    });
  };

  // Helper function to get sport from location state or URL params using shared utility
  const getSportFromLocation = useCallback((): string => {
    return getSportValueUtil(tournament, location);
  }, [tournament, location]);

  // Load seasons
  // OPTIMIZATION: Call API only once and filter results locally instead of making 2 API calls
  // Also use ref to prevent concurrent calls
  // IMPROVEMENT: Get sport from location state/URL params instead of tournament object
  // This allows loading seasons even before tournament details are loaded
  const loadSeasons = useCallback(async (id: string) => {
    if (!id) {
      setSeasons([]);
      return;
    }

    // Prevent duplicate concurrent calls
    if (isLoadingSeasonsRef.current) {
      return;
    }

    try {
      isLoadingSeasonsRef.current = true;
      setSeasonsLoading(true);
      setIsRedirecting(false);
      
      // IMPROVEMENT: Get sport from location state/URL params (same as loadTournamentDetails)
      // This ensures we have the correct sport even if tournament isn't loaded yet
      const sport = getSportFromLocation();
      
      // OPTIMIZATION: Call API only once - get all seasons including DEFAULT
      // Then filter locally instead of making a second API call
      const allSeasons = await getAllTournamentSeasons(id, sport);
      
      // Check if tournament has DEFAULT season
      const defaultSeason = allSeasons.find((s: TournamentSeason) => s.seasonType === 'DEFAULT');
      setHasDefaultSeason(!!defaultSeason);
      
      // Keep DEFAULT and regular seasons so every tournament season can be managed here.
      const sortedSeasons = sortSeasonsByStatus(allSeasons);
      
      setSeasons(sortedSeasons);
    } catch (error: any) {
      console.error('❌ Error loading seasons:', error);
      setSeasons([]);
      setHasDefaultSeason(false);
    } finally {
      setSeasonsLoading(false);
      isLoadingSeasonsRef.current = false;
    }
  }, [getSportFromLocation]);

  // Handle tournament selection
  const handleTournamentChange = (selectedOption: any) => {
    const newTournamentId = selectedOption ? selectedOption.value : null;
    setSelectedTournamentId(newTournamentId);
    
    if (newTournamentId) {
      // Reset ref to allow loading new tournament
      lastLoadedTournamentIdRef.current = null;
      // Update URL without navigation
      window.history.replaceState({}, '', `/manage-tournaments-fixtures/${newTournamentId}/seasons`);
      loadTournamentDetails(newTournamentId);
      loadSeasons(newTournamentId);
    } else {
      setTournament(null);
      setSeasons([]);
      lastLoadedTournamentIdRef.current = null;
    }
  };

  // Load tournaments list on mount
  useEffect(() => {
    if (canAccessFixtures) {
      loadAllTournaments();
    }
  }, [canAccessFixtures]);

  // Load tournament details and seasons when tournamentId changes or on initial load
  // OPTIMIZATION: Prevent duplicate calls by tracking the last loaded tournament ID
  // IMPROVEMENT: Simplified logic and better separation of concerns
  // FIX: Check if tournament is actually loaded before skipping to handle back navigation
  useEffect(() => {
    // Early return if user doesn't have access (but don't include in deps to avoid Redux-triggered re-runs)
    if (!canAccessFixtures) {
      return;
    }

    // Determine which tournament ID to use
    const targetTournamentId = selectedTournamentId || tournamentId;
    
    if (!targetTournamentId) {
      return;
    }

    // FIX: Check if tournament is actually loaded before skipping
    // If tournament is null/undefined, we need to reload even if ref says it's loaded
    // This handles the case when navigating back from season detail page where location state might be empty
    const isTournamentLoaded = tournament !== null && tournament !== undefined;
    const shouldSkip = lastLoadedTournamentIdRef.current === targetTournamentId && isTournamentLoaded;
    
    if (shouldSkip) {
      return;
    }

    // Handle initial load: sync selectedTournamentId with tournamentId from URL
    if (!initialLoadDoneRef.current) {
      if (!selectedTournamentId && tournamentId) {
        // Set selectedTournamentId to match URL param - this will trigger effect again
        setSelectedTournamentId(tournamentId);
        initialLoadDoneRef.current = true;
        return; // Exit early, effect will run again with selectedTournamentId set
      }
      initialLoadDoneRef.current = true;
    }

    // Mark this tournament ID as loaded to prevent duplicate calls
    lastLoadedTournamentIdRef.current = targetTournamentId;

    // Load tournament details and seasons in parallel
    // Both can run independently since loadSeasons no longer depends on tournament object
    loadTournamentDetails(targetTournamentId);
    loadSeasons(targetTournamentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTournamentId, tournamentId, loadSeasons]);

  // FIX: Reset ref when tournament becomes null to allow reloading on back navigation
  // This ensures that when navigating back from season detail page, tournament can be reloaded
  useEffect(() => {
    if (tournament === null && lastLoadedTournamentIdRef.current !== null) {
      // Tournament was cleared (e.g., on back navigation), reset ref to allow reloading
      lastLoadedTournamentIdRef.current = null;
    }
  }, [tournament]);

  // Filter seasons based on status filter (already sorted: ongoing first, then completed)
  const filteredSeasons = seasons.filter(season => {
    const matchesStatus = !statusFilter || 
      season.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesStatus;
  });

  // Format date for display
  const formatDisplayDate = (dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}${getDaySuffix(day)} ${month}, ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const getDaySuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Get status badge color - matching tournaments listing style
  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    const isCancelled = statusLower === 'cancelled';
    
    return (
      <Badge 
        bg={statusLower === 'completed' ? 'success' : 
            statusLower === 'ongoing' ? 'primary' : 
            statusLower === 'upcoming' ? 'warning' : 
            isCancelled ? 'danger' : 'secondary'}
        className="payment-badge"
        style={{
          border: '1px solid',
          fontWeight: '500',
          padding: '0.375rem 0.75rem',
          borderRadius: '6px',
          backgroundColor: statusLower === 'completed' ? '#E9FCF2' : 
                         statusLower === 'ongoing' ? '#E6F1FF' : 
                         statusLower === 'upcoming' ? '#FFF5EC' : 
                         isCancelled ? '#FEE' : '#F8F9FA',
          color: statusLower === 'completed' ? '#1B9E5C' : 
                 statusLower === 'ongoing' ? '#006EFF' : 
                 statusLower === 'upcoming' ? '#FD9644' : 
                 isCancelled ? '#DC3545' : '#6C757D',
          borderColor: statusLower === 'completed' ? '#1B9E5C' : 
                      statusLower === 'ongoing' ? '#006EFF' : 
                      statusLower === 'upcoming' ? '#FD9644' : 
                      isCancelled ? '#DC3545' : '#6C757D',
          textTransform: 'capitalize'
        }}
      >
        {isCancelled ? 'cancelled' : (status || 'Unknown')}
      </Badge>
    );
  };

  // Handle season actions
  const handleViewSeason = (season: TournamentSeason) => {
    if (!selectedTournamentId) return;
    navigate(`/manage-tournaments-fixtures/${selectedTournamentId}/seasons/${season._id}`, {
      state: {
        viewMode: true,
        selectedSeason: season,
        selectedTournament: tournament
      }
    });
  };

  const handleEditSeason = (season: TournamentSeason) => {
    if (!selectedTournamentId) return;
    // Navigate to season detail page for editing
    navigate(`/manage-tournaments-fixtures/${selectedTournamentId}/seasons/${season._id}`, {
      state: {
        viewMode: false, // Edit mode
        selectedSeason: season,
        selectedTournament: tournament,
        activeTab: 'details' // Open details tab for editing
      }
    });
  };

  // Handle cancel season edit
  const handleCancelSeasonEdit = () => {
    setIsEditingSeason(false);
    setEditingSeason(null);
    setSeasonFormData({
      name: '',
      startDate: '',
      endDate: '',
      location: '',
      seasonYearRange: '',
      isFeaturedOnTv: false,
      showOnHomePage: true,
      tvSortOrder: '2',
      tvPreviewDuration: '',
      sortOrder: '2',
      tvAccessType: 'paid',
      recordingAccessType: 'free',
      highlightAccessType: 'free',
      matchPassEnabled: false,
      matchPassPrice: '',
      seasonPassEnabled: false,
      seasonPassPrice: '',
      tournamentPassEnabled: false,
      heroImage: null,
      detailHeroImage: null,
      detailHeroImageApp: null,
      matchCardBackgroundImage: null,
    });
    setSeasonMediaTouched({ heroImage: false, detailHeroImage: false, detailHeroImageApp: false, matchCardBackgroundImage: false });
    setSeasonFormErrors({});
    setApiError(null);
  };

  // Check if tournament can be deleted (not ongoing or completed)
  const canDeleteTournament = (tournament: ManageTournament | null): boolean => {
    if (!tournament) return false;
    const status = (tournament.status || '').toLowerCase();
    return status !== 'ongoing' && status !== 'completed';
  };

  // Check if season can be deleted (not ongoing or completed)
  const canDeleteSeason = (season: TournamentSeason): boolean => {
    const status = (season.status || '').toLowerCase();
    return status !== 'ongoing' && status !== 'completed';
  };

  const handleDeleteSeason = (season: TournamentSeason) => {
    if (!canDeleteSeason(season)) {
      return;
    }
    setDeleteSeasonTarget(season);
    setDeleteSeasonError(null);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (!isDeletingSeason) {
      setShowDeleteModal(false);
      setDeleteSeasonTarget(null);
      setDeleteSeasonError(null);
    }
  };

  const confirmDeleteSeason = async () => {
    if (!deleteSeasonTarget) {
      return;
    }

    try {
      setIsDeletingSeason(true);
      setDeleteSeasonError(null);
      await deleteTournamentSeason(deleteSeasonTarget._id, getSportFromLocation());
      setSuccessMessage('Season deleted successfully!');
      await loadSeasons(selectedTournamentId || '');
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowDeleteModal(false);
      setDeleteSeasonTarget(null);
    } catch (error: any) {
      setDeleteSeasonError(error.message || 'Failed to delete season');
    } finally {
      setIsDeletingSeason(false);
    }
  };

  const handleAddNewSeason = () => {
    setEditingSeason(null);
    setIsEditingSeason(true);
    setSeasonFormData({
      name: '',
      startDate: '',
      endDate: '',
      location: tournament?.location || '',
      seasonYearRange: '',
      isFeaturedOnTv: false,
      showOnHomePage: true,
      tvSortOrder: '2',
      tvPreviewDuration: '',
      sortOrder: '2',
      tvAccessType: 'paid',
      recordingAccessType: 'free',
      highlightAccessType: 'free',
      matchPassEnabled: false,
      matchPassPrice: '',
      seasonPassEnabled: false,
      seasonPassPrice: '',
      tournamentPassEnabled: false,
      heroImage: null,
      detailHeroImage: null,
      detailHeroImageApp: null,
      matchCardBackgroundImage: null,
    });
    setSeasonMediaTouched({ heroImage: false, detailHeroImage: false, detailHeroImageApp: false, matchCardBackgroundImage: false });
    setSeasonFormErrors({});
    setApiError(null);
    setSuccessMessage(null);
  };

  const validateSeasonForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!seasonFormData.name || seasonFormData.name.trim().length === 0) {
      errors.name = 'Season name is required';
    }

    if (!seasonFormData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (!seasonFormData.endDate) {
      errors.endDate = 'End date is required';
    }

    if (!seasonFormData.location || seasonFormData.location.trim().length === 0) {
      errors.location = 'Location is required';
    } else {
      if (!SEASON_LOCATION_PATTERN.test(seasonFormData.location.trim())) {
        errors.location = SEASON_LOCATION_ERROR_MESSAGE;
      }
    }

    if (!seasonFormData.seasonYearRange || seasonFormData.seasonYearRange.trim().length === 0) {
      errors.seasonYearRange = 'Season year range is required';
    } else {
      const yearRangePattern = /^(\d{4})(-\d{4})?$/;
      const trimmedRange = seasonFormData.seasonYearRange.trim();
      if (!yearRangePattern.test(trimmedRange)) {
        errors.seasonYearRange = 'Season year range must be in format "2025" or "2025-2026"';
      } else {
        if (trimmedRange.includes('-')) {
          const [startYear, endYear] = trimmedRange.split('-').map(Number);
          if (endYear < startYear) {
            errors.seasonYearRange = 'End year must be greater than or equal to start year';
          }
        }
      }
    }

    if (seasonFormData.startDate && seasonFormData.endDate) {
      Object.assign(errors, validateSeasonDates(seasonFormData.startDate, seasonFormData.endDate));
    }

    setSeasonFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Calculate season year range from dates
  const calculateSeasonYearRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    const startYear = new Date(startDate).getFullYear();
    const endYear = new Date(endDate).getFullYear();
    return `${startYear}-${endYear}`;
  };

  // Calculate next season number
  const calculateNextSeasonNumber = (existingSeasons: TournamentSeason[], hasDefault: boolean): number => {
    // If tournament has DEFAULT season and no SEASON type seasons exist yet, start from 2
    if (hasDefault && existingSeasons.length === 0) {
      return 2;
    }
    
    // If no seasons exist and no DEFAULT season, start from 1
    if (existingSeasons.length === 0) {
      return 1;
    }
    
    // Otherwise, find max seasonNumber from existing SEASON type seasons and add 1
    const maxSeasonNumber = Math.max(...existingSeasons.map(s => s.seasonNumber || 0));
    return maxSeasonNumber + 1;
  };

  const handleSeasonMediaChange = (
    field: 'heroImage' | 'detailHeroImage' | 'detailHeroImageApp' | 'matchCardBackgroundImage',
    value: string | null,
  ) => {
    setSeasonFormData((current) => ({ ...current, [field]: value }));
    setSeasonMediaTouched((current) => ({ ...current, [field]: true }));
  };

  const handleSeasonMediaUploadingChange = (
    field: 'heroImage' | 'detailHeroImage' | 'detailHeroImageApp' | 'matchCardBackgroundImage',
    uploading: boolean,
  ) => {
    setSeasonMediaUploading((current) => ({ ...current, [field]: uploading }));
  };

  const getSeasonMediaContext = (variant: 'portrait' | 'horizontal' | 'horizontal-app' | 'match-card-background') =>
    `tournament-seasons/${editingSeason?._id || 'new'}/media/${variant}`;

  const buildSeasonMediaPayload = () => {
    const payload: {
      heroImage?: string | null;
      detailHeroImage?: string | null;
      detailHeroImageApp?: string | null;
      matchCardBackgroundImage?: string | null;
    } = {};

    if (SEASON_VERTICAL_MEDIA_ENABLED && seasonMediaTouched.heroImage) {
      payload.heroImage = seasonFormData.heroImage;
    }
    if (seasonMediaTouched.detailHeroImage) {
      payload.detailHeroImage = seasonFormData.detailHeroImage;
    }
    if (seasonMediaTouched.detailHeroImageApp) {
      payload.detailHeroImageApp = seasonFormData.detailHeroImageApp;
    }
    if (seasonMediaTouched.matchCardBackgroundImage) {
      payload.matchCardBackgroundImage = seasonFormData.matchCardBackgroundImage;
    }

    return payload;
  };

  // Handle season form submission
  const handleSeasonFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateSeasonForm() || !selectedTournamentId) {
      return;
    }

    try {
      setSeasonFormLoading(true);
      setApiError(null);
      setSuccessMessage(null);

      const startDate = seasonFormData.startDate;
      const endDate = seasonFormData.endDate;
      const seasonYearRange = seasonFormData.seasonYearRange.trim() || calculateSeasonYearRange(startDate, endDate);
      const location = seasonFormData.location.trim();

      if (editingSeason) {
        // Edit existing season - preserve seasonNumber and seasonType from existing season
        await editTournamentSeason(editingSeason._id, getSportFromLocation(), {
          seasonType: editingSeason.seasonType || 'SEASON',
          seasonNumber: editingSeason.seasonNumber || 1,
          seasonLabel: seasonFormData.name.trim(),
          seasonYearRange: seasonYearRange,
          location: location,
          startDate: startDate,
          endDate: endDate,
          isFeaturedOnTv: seasonFormData.isFeaturedOnTv,
          showOnHomePage: seasonFormData.showOnHomePage,
          tvSortOrder: Number(seasonFormData.tvSortOrder),
          tvPreviewDuration: seasonFormData.tvPreviewDuration === '' ? null : Number(seasonFormData.tvPreviewDuration),
          sortOrder: Number(seasonFormData.sortOrder),
          tvAccessType: seasonFormData.tvAccessType as 'free' | 'paid',
          recordingAccessType: seasonFormData.recordingAccessType as 'free' | 'paid',
          highlightAccessType: seasonFormData.highlightAccessType as 'free' | 'paid',
          tvPassConfig: buildTvPassConfig({
            matchEnabled: seasonFormData.tvAccessType === 'paid' && seasonFormData.matchPassEnabled,
            matchPrice: seasonFormData.matchPassPrice,
            seasonEnabled: seasonFormData.seasonPassEnabled,
            seasonPrice: seasonFormData.seasonPassPrice,
            tournamentEnabled: seasonFormData.tournamentPassEnabled,
          }),
          ...buildSeasonMediaPayload(),
        });
        setSuccessMessage('Season updated successfully!');
      } else {
        // Add new season - calculate seasonNumber from existing seasons
        const nextSeasonNumber = calculateNextSeasonNumber(seasons, hasDefaultSeason);
        await addTournamentSeason(selectedTournamentId, getSportFromLocation(), {
          seasonType: 'SEASON',
          seasonNumber: nextSeasonNumber,
          seasonLabel: seasonFormData.name.trim(),
          seasonYearRange: seasonYearRange,
          location: location,
          startDate: startDate,
          endDate: endDate,
          ageCategories: [],
          milestonePayment: [],
          pricing: [],
          isFeaturedOnTv: seasonFormData.isFeaturedOnTv,
          showOnHomePage: seasonFormData.showOnHomePage,
          tvSortOrder: Number(seasonFormData.tvSortOrder),
          tvPreviewDuration: seasonFormData.tvPreviewDuration === '' ? null : Number(seasonFormData.tvPreviewDuration),
          sortOrder: Number(seasonFormData.sortOrder),
          tvAccessType: seasonFormData.tvAccessType as 'free' | 'paid',
          recordingAccessType: seasonFormData.recordingAccessType as 'free' | 'paid',
          highlightAccessType: seasonFormData.highlightAccessType as 'free' | 'paid',
          tvPassConfig: buildTvPassConfig({
            matchEnabled: seasonFormData.tvAccessType === 'paid' && seasonFormData.matchPassEnabled,
            matchPrice: seasonFormData.matchPassPrice,
            seasonEnabled: seasonFormData.seasonPassEnabled,
            seasonPrice: seasonFormData.seasonPassPrice,
            tournamentEnabled: seasonFormData.tournamentPassEnabled,
          }),
          ...buildSeasonMediaPayload(),
        });
        setSuccessMessage('Season added successfully!');
      }

      setIsEditingSeason(false);
      setEditingSeason(null);
      await loadSeasons(selectedTournamentId);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      const errorMessage = error.message || `Failed to ${editingSeason ? 'update' : 'add'} season`;
      setApiError(errorMessage);
      if (/already exists/i.test(errorMessage)) {
        setSeasonFormErrors((current) => ({
          ...current,
          name: errorMessage,
        }));
      }
    } finally {
      setSeasonFormLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/manage-tournaments-fixtures');
  };

  // Validation functions
  const validateField = (field: string, value: any, currentData?: TournamentPayload): string => {
    const data = currentData || inlineEditData;
    
    switch (field) {
      case 'name':
        if (!value || value.trim().length === 0) {
          return 'Tournament name is required';
        }
        if (value.trim().length < 3) {
          return 'Tournament name must be at least 3 characters long';
        }
        if (value.trim().length > 100) {
          return 'Tournament name must be less than 100 characters';
        }
        break;
      case 'shortName':
        if (!value || value.trim().length === 0) {
          return 'Short name is required';
        }
        if (value.trim().length < 2) {
          return 'Short name must be at least 2 characters long';
        }
        if (value.trim().length > 20) {
          return 'Short name must be less than 20 characters';
        }
        break;
      case 'status':
        if (!value || value.trim().length === 0) {
          return 'Status is required';
        }
        const validStatuses = ['upcoming', 'ongoing', 'completed', 'cancelled'];
        if (!validStatuses.includes(value)) {
          return 'Please select a valid status';
        }
        break;
      case 'startDate':
        if (!value) {
          return 'Start date is required';
        }
        break;
      case 'endDate':
        if (!value) {
          return 'End date is required';
        }
        if (data.startDate && value && new Date(value) < new Date(data.startDate)) {
          return 'End date must be after start date';
        }
        break;
    }
    return '';
  };

  const validateForm = (data: TournamentPayload): {[key: string]: string} => {
    const errors: {[key: string]: string} = {};
    
    const nameError = validateField('name', data.name, data);
    if (nameError) errors.name = nameError;
    
    const shortNameError = validateField('shortName', data.shortName, data);
    if (shortNameError) errors.shortName = shortNameError;
    
    const statusError = validateField('status', data.status, data);
    if (statusError) errors.status = statusError;
    
    const startDateError = validateField('startDate', data.startDate, data);
    if (startDateError) errors.startDate = startDateError;
    
    const endDateError = validateField('endDate', data.endDate, data);
    if (endDateError) errors.endDate = endDateError;
    
    return errors;
  };

  // Handle edit button click - Start inline editing
  const handleEditClick = () => {
    if (tournament) {
      setInlineEditData({
        _id: tournament._id,
        name: tournament.name,
        shortName: tournament.shortName,
        logo: tournament.logo || '',
        location: tournament.location,
        startDate: tournament.startDate,
        endDate: tournament.endDate,
        status: tournament.status,
        sports: tournament.sports,
        sortNo: 1
      });
      setIsEditing(true);
      setIsDetailsExpanded(true); // Expand details section when editing
      setValidationErrors({});
      setApiError(null);
      setSuccessMessage(null);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setInlineEditData({
      name: '',
      shortName: '',
      logo: '',
      location: '',
      startDate: '',
      endDate: '',
      status: '',
      sports: '',
      sortNo: 1
    });
    setValidationErrors({});
    setApiError(null);
    setSuccessMessage(null);
  };

  // Handle inline edit form submission
  const handleInlineEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineEditData._id) return;

    setApiError(null);
    setSuccessMessage(null);

    const errors = validateForm(inlineEditData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});

    try {
      setEditLoading(true);
      await editTournament(inlineEditData);
      setIsEditing(false);
      setSuccessMessage('Tournament updated successfully!');
      // Reload tournament details using the selected tournament ID
      if (selectedTournamentId) {
        await loadTournamentDetails(selectedTournamentId);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error updating tournament:', err);
      let errorMessage = 'Failed to update tournament';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setApiError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  // Handle field change with validation
  const handleFieldChange = (field: string, value: any) => {
    const updatedData = { ...inlineEditData, [field]: value };
    setInlineEditData(updatedData);
    
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    if (apiError) {
      setApiError(null);
    }

    if (field === 'name' || field === 'shortName' || field === 'status' || field === 'startDate' || field === 'endDate') {
      const error = validateField(field, value, updatedData);
      if (error) {
        setValidationErrors(prev => ({ ...prev, [field]: error }));
      }
    }

    if (field === 'status') {
      if (updatedData.startDate) {
        const startDateError = validateField('startDate', updatedData.startDate, updatedData);
        if (startDateError) {
          setValidationErrors(prev => ({ ...prev, startDate: startDateError }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.startDate;
            return newErrors;
          });
        }
      }
      if (updatedData.endDate) {
        const endDateError = validateField('endDate', updatedData.endDate, updatedData);
        if (endDateError) {
          setValidationErrors(prev => ({ ...prev, endDate: endDateError }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.endDate;
            return newErrors;
          });
        }
      }
    }
  };

  // Access denied for non-authorized users
  if (!canAccessFixtures) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  // Build breadcrumb items with actual tournament name
  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { 
      label: 'Manage Tournaments', 
      onClick: () => navigate('/manage-tournaments-fixtures', {
        state: {
          allTournaments: (location.state as any)?.allTournaments
        }
      })
    },
    ...(tournament ? [
      { 
        label: tournament.name || 'Tournament',
        onClick: () => navigate(`/manage-tournaments-fixtures/${tournamentId}`, {
          state: {
            selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
            allTournaments: (location.state as any)?.allTournaments
          }
        })
      }
    ] : []),
    { label: 'Seasons', active: true }
  ];

  // Show loading for tournaments list
  if (tournamentsLoading && tournaments.length === 0) {
    return (
      <div className="page-content">
        <Container fluid>
          <ShimmerLoader type="table" />
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>


        {/* Tournament Header */}
        {tournament && (
          <div style={{ padding: '0 15px' }}>
            <TournamentHeader 
              tournament={tournament} 
              onGoBack={handleGoBack}
              showBackButton={true}
            />
          </div>
        )}

        {/* Tournament Details Card - Collapsible Dropdown */}
        {tournament && (
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <Card>
              <Card.Header 
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
              >
                <div className="d-flex align-items-center">
                  <i 
                    className={`ri-arrow-${isDetailsExpanded ? 'down' : 'right'}-s-line me-2`}
                    style={{ fontSize: '18px', color: '#6c757d' }}
                  ></i>
                  <h4 className="mb-0 card-title">Details</h4>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  {!isEditing ? (
                    <>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick();
                        }}
                        className="me-2"
                        style={{ borderRadius: '6px' }}
                      >
                        <i className="ri-pencil-line me-1"></i>
                        Edit
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Double check if deletion is allowed
                          if (!canDeleteTournament(tournament)) {
                            return;
                          }
                          if (window.confirm('Are you sure you want to delete this tournament?')) {
                            // Delete functionality to be implemented
                          }
                        }}
                        disabled={!canDeleteTournament(tournament)}
                        title={canDeleteTournament(tournament) ? "Delete Tournament" : "Cannot delete ongoing or completed tournaments"}
                        style={{ 
                          borderRadius: '6px',
                          opacity: canDeleteTournament(tournament) ? 1 : 0.5,
                          cursor: canDeleteTournament(tournament) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <i className="ri-delete-bin-line me-1"></i>
                        Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelEdit();
                        }}
                        className="me-2"
                        style={{ borderRadius: '6px' }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInlineEditSubmit(e);
                        }}
                        disabled={editLoading}
                        style={{ borderRadius: '6px' }}
                      >
                        {editLoading ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  )}
                </div>
              </Card.Header>
              {isDetailsExpanded && (
                <Card.Body>
                  {/* Success Message */}
                {successMessage && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    <i className="ri-check-line me-2"></i>
                    {successMessage}
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setSuccessMessage(null)}
                    ></button>
                  </div>
                )}

                {/* API Error Message */}
                {apiError && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="ri-error-warning-line me-2"></i>
                    {apiError}
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setApiError(null)}
                    ></button>
                  </div>
                )}

                {/* Validation Errors Summary */}
                {isEditing && Object.keys(validationErrors).length > 0 && (
                  <div className="alert alert-warning alert-dismissible fade show" role="alert">
                    <i className="ri-alert-line me-2"></i>
                    <strong>Please fix the following errors:</strong>
                    <ul className="mb-0 mt-2">
                      {Object.entries(validationErrors).map(([field, error]) => (
                        <li key={field}>
                          <strong>{field.charAt(0).toUpperCase() + field.slice(1)}:</strong> {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isEditing ? (
                  /* Inline Edit Form */
                  <Form onSubmit={handleInlineEditSubmit}>
                    <Row>
                      <Col md={6}>
                        <LogoUpload
                          currentLogo={inlineEditData.logo}
                          onLogoChange={(logoUrl) => handleFieldChange('logo', logoUrl)}
                          onLogoRemove={() => handleFieldChange('logo', '')}
                          isEditMode={true}
                          context="tournaments/logo"
                          size={60}
                          title="Tournament Logo"
                          label="Tournament Logo"
                        />
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Tournament Name <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={inlineEditData.name}
                            onChange={(e) => handleFieldChange('name', e.target.value)}
                            isInvalid={!!validationErrors.name}
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: validationErrors.name ? '#dc3545' : undefined
                            }}
                            placeholder="Enter tournament name"
                          />
                          {validationErrors.name && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.name}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Tournament Short Name <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={inlineEditData.shortName}
                            onChange={(e) => handleFieldChange('shortName', e.target.value)}
                            isInvalid={!!validationErrors.shortName}
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: validationErrors.shortName ? '#dc3545' : undefined
                            }}
                            placeholder="Enter short name"
                          />
                          {validationErrors.shortName && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.shortName}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Sports
                          </label>
                          <Form.Control
                            type="text"
                            value={inlineEditData.sports}
                            readOnly
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#6c757d',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #e9ecef',
                              cursor: 'not-allowed'
                            }}
                            placeholder="Sports type (read-only)"
                          />
                          <small className="text-muted">
                            <i className="ri-lock-line me-1"></i>
                            Sports field cannot be edited
                          </small>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Status <span className="text-danger">*</span>
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            value={inlineEditData.status ? { value: inlineEditData.status, label: inlineEditData.status.charAt(0).toUpperCase() + inlineEditData.status.slice(1) } : null}
                            onChange={(selectedOption) => {
                              handleFieldChange('status', selectedOption ? selectedOption.value : '');
                            }}
                            options={[
                              { value: 'upcoming', label: 'Upcoming' },
                              { value: 'ongoing', label: 'Ongoing' },
                              { value: 'completed', label: 'Completed' },
                              { value: 'cancelled', label: 'Cancelled' }
                            ]}
                            placeholder="Select status"
                            maxMenuHeight={200}
                            menuPosition="fixed"
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base, state) => ({ 
                                ...base, 
                                minHeight: '38px',
                                border: validationErrors.status ? '1px solid #dc3545' : '1px solid #e0e0e0',
                                borderRadius: '6px',
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529',
                                boxShadow: state.isFocused ? (validationErrors.status ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : '0 0 0 0.2rem rgba(0, 123, 255, 0.25)') : 'none'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 99999,
                                maxHeight: 200,
                                overflow: 'hidden'
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 99999 
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: 200,
                                padding: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden'
                              }),
                              option: (base, state) => ({
                                ...base,
                                padding: '8px 12px',
                                fontSize: '14px',
                                backgroundColor: state.isFocused ? '#e3f2fd' : 'white',
                                color: state.isFocused ? '#1976d2' : '#212529'
                              }),
                              singleValue: (base) => ({
                                ...base,
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#212529'
                              })
                            }}
                          />
                          {validationErrors.status && (
                            <div className="invalid-feedback d-block">
                              {validationErrors.status}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Start Date <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="date"
                            value={inlineEditData.startDate ? inlineEditData.startDate.split('T')[0] : ''}
                            onChange={(e) => handleFieldChange('startDate', e.target.value + 'T00:00:00.000Z')}
                            isInvalid={!!validationErrors.startDate}
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: validationErrors.startDate ? '#dc3545' : undefined
                            }}
                          />
                          {validationErrors.startDate && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.startDate}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            End Date <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="date"
                            value={inlineEditData.endDate ? inlineEditData.endDate.split('T')[0] : ''}
                            onChange={(e) => handleFieldChange('endDate', e.target.value + 'T00:00:00.000Z')}
                            isInvalid={!!validationErrors.endDate}
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: validationErrors.endDate ? '#dc3545' : undefined
                            }}
                          />
                          {validationErrors.endDate && (
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.endDate}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Location
                          </label>
                          <Form.Control
                            type="text"
                            value={inlineEditData.location}
                            onChange={(e) => handleFieldChange('location', e.target.value)}
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529'
                            }}
                            placeholder="Enter location"
                          />
                        </div>
                      </Col>
                    </Row>
                  </Form>
                ) : (
                  /* Display Mode */
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{
                          fontSize: '14px',
                          color: '#6c757d',
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Tournament Logo
                        </label>
                        <div>
                          {tournament.logo ? (
                            <img 
                              src={getTournamentLogoUrl(tournament) || ''} 
                              alt="Tournament Logo" 
                              style={{ 
                                width: '60px', 
                                height: '60px', 
                                borderRadius: '50%', 
                                objectFit: 'cover',
                                border: '2px solid #e9ecef'
                              }}
                            />
                          ) : (
                            <div 
                              style={{ 
                                width: '60px', 
                                height: '60px', 
                                borderRadius: '50%', 
                                backgroundColor: '#f8f9fa',
                                border: '2px solid #dee2e6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <i className="ri-image-line" style={{ fontSize: '1.5rem', color: '#6c757d' }}></i>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{ 
                          fontSize: '14px', 
                          color: '#6c757d', 
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Tournament Name
                        </label>
                        <div style={{ fontSize: '16px', fontWeight: '500', color: '#212529' }}>
                          {tournament.name}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{ 
                          fontSize: '14px', 
                          color: '#6c757d', 
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Tournament Short Name
                        </label>
                        <div style={{ fontSize: '16px', fontWeight: '500', color: '#212529' }}>
                          {tournament.shortName}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{ 
                          fontSize: '14px', 
                          color: '#6c757d', 
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Sports
                        </label>
                        <div style={{ fontSize: '16px', fontWeight: '500', color: '#212529' }}>
                          {tournament.sports}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{ 
                          fontSize: '14px', 
                          color: '#6c757d', 
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Status
                        </label>
                        <div>
                          {getStatusBadge(tournament.status)}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{ 
                          fontSize: '14px', 
                          color: '#6c757d', 
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Start Date
                        </label>
                        <div style={{ fontSize: '16px', fontWeight: '500', color: '#212529' }}>
                          {tournament.startDate ? formatDate(tournament.startDate) : '-'}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{ 
                          fontSize: '14px', 
                          color: '#6c757d', 
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          End Date
                        </label>
                        <div style={{ fontSize: '16px', fontWeight: '500', color: '#212529' }}>
                          {tournament.endDate ? formatDate(tournament.endDate) : '-'}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <label className="form-label" style={{ 
                          fontSize: '14px', 
                          color: '#6c757d', 
                          fontWeight: '500',
                          marginBottom: '8px'
                        }}>
                          Location
                        </label>
                        <div style={{ fontSize: '16px', fontWeight: '500', color: '#212529' }}>
                          {tournament.location || '-'}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
                </Card.Body>
              )}
            </Card>
          </div>
        )}

        {/* Season Listing Section - Only show if no DEFAULT season or if there are SEASON type seasons */}
        {selectedTournamentId && (!hasDefaultSeason || seasons.length > 0 || isEditingSeason) && (
          <div style={{ padding: '0 15px' }}>
            <Row className="align-items-center mb-3">
              <Col md={6}>
                <h2 className="page-title mb-0">Season Listing</h2>
              </Col>
              <Col md={6} className="text-end">
                <div className="d-flex align-items-center justify-content-end gap-3">
                  {/* Status Filter */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label className="mb-0" style={{ fontSize: '14px', fontWeight: '500', color: '#6c757d' }}>
                      Filter by Status:
                    </label>
                    <Select
                      classNamePrefix="react-select"
                      value={statusFilter ? { value: statusFilter, label: statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) } : null}
                      onChange={(selectedOption) => setStatusFilter(selectedOption ? selectedOption.value : '')}
                      options={[
                        { value: '', label: 'All' },
                        { value: 'upcoming', label: 'Upcoming' },
                        { value: 'ongoing', label: 'Ongoing' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'cancelled', label: 'Cancelled' }
                      ]}
                      placeholder="All"
                      isClearable
                      maxMenuHeight={200}
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({ 
                          ...base, 
                          minHeight: '38px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px',
                          fontSize: '14px',
                          width: '150px'
                        }),
                        menu: (base) => ({ 
                          ...base, 
                          zIndex: 99999,
                          maxHeight: 200,
                          overflow: 'hidden'
                        }),
                        menuPortal: (base) => ({ 
                          ...base, 
                          zIndex: 99999 
                        }),
                        menuList: (base) => ({
                          ...base,
                          maxHeight: 200,
                          padding: 0,
                          overflowY: 'auto',
                          overflowX: 'hidden'
                        }),
                        option: (base, state) => ({
                          ...base,
                          padding: '8px 12px',
                          fontSize: '14px',
                          backgroundColor: state.isFocused ? '#e3f2fd' : 'white',
                          color: state.isFocused ? '#1976d2' : '#212529'
                        })
                      }}
                    />
                  </div>
                  <Button 
                    variant="primary" 
                    onClick={handleAddNewSeason}
                    style={{ borderRadius: '6px' }}
                  >
                    <i className="ph-plus me-2"></i>
                    Add New Season
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Success Message */}
            {successMessage && (
              <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
                <i className="ri-check-line me-2"></i>
                {successMessage}
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSuccessMessage(null)}
                ></button>
              </div>
            )}

            {/* API Error Message */}
            {apiError && (
              <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                <i className="ri-error-warning-line me-2"></i>
                {apiError}
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setApiError(null)}
                ></button>
              </div>
            )}

            {/* Inline Season Form */}
            {isEditingSeason ? (
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 card-title">{editingSeason ? 'Edit Season' : 'Add New Season'}</h4>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSeasonFormSubmit}>
                    <Row>
                      <Col md={12}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Season Name <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={seasonFormData.name}
                            onChange={(e) => {
                              setSeasonFormData({ ...seasonFormData, name: e.target.value });
                              if (apiError && /already exists/i.test(apiError)) {
                                setApiError(null);
                              }
                              if (seasonFormErrors.name) {
                                setSeasonFormErrors({ ...seasonFormErrors, name: '' });
                              }
                            }}
                            isInvalid={!!seasonFormErrors.name}
                            placeholder="Enter season name (e.g., season-1)"
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: seasonFormErrors.name ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.name && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.name}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Start Date <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="date"
                            value={seasonFormData.startDate}
                            onChange={(e) => {
                              setSeasonFormData({ ...seasonFormData, startDate: e.target.value });
                              if (seasonFormErrors.startDate) {
                                setSeasonFormErrors({ ...seasonFormErrors, startDate: '' });
                              }
                              // Re-validate end date if it exists
                              if (seasonFormData.endDate) {
                                const endDateError = new Date(seasonFormData.endDate) < new Date(e.target.value)
                                  ? 'End date must be after start date'
                                  : '';
                                if (endDateError) {
                                  setSeasonFormErrors({ ...seasonFormErrors, endDate: endDateError });
                                } else if (seasonFormErrors.endDate) {
                                  const newErrors = { ...seasonFormErrors };
                                  delete newErrors.endDate;
                                  setSeasonFormErrors(newErrors);
                                }
                              }
                              if (seasonFormData.endDate && !seasonFormData.seasonYearRange) {
                                const calculatedRange = calculateSeasonYearRange(e.target.value, seasonFormData.endDate);
                                setSeasonFormData(prev => ({ ...prev, seasonYearRange: calculatedRange }));
                              }
                            }}
                            isInvalid={!!seasonFormErrors.startDate}
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: seasonFormErrors.startDate ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.startDate && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.startDate}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            End Date <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="date"
                            value={seasonFormData.endDate}
                            min={seasonFormData.startDate || undefined}
                            onChange={(e) => {
                              setSeasonFormData({ ...seasonFormData, endDate: e.target.value });
                              if (seasonFormErrors.endDate) {
                                setSeasonFormErrors({ ...seasonFormErrors, endDate: '' });
                              }
                              if (seasonFormData.startDate && new Date(e.target.value) < new Date(seasonFormData.startDate)) {
                                setSeasonFormErrors({ ...seasonFormErrors, endDate: 'End date must be after start date' });
                              } else if (seasonFormErrors.endDate) {
                                const newErrors = { ...seasonFormErrors };
                                delete newErrors.endDate;
                                setSeasonFormErrors(newErrors);
                              }
                              if (seasonFormData.startDate && !seasonFormData.seasonYearRange) {
                                const calculatedRange = calculateSeasonYearRange(seasonFormData.startDate, e.target.value);
                                setSeasonFormData(prev => ({ ...prev, seasonYearRange: calculatedRange }));
                              }
                            }}
                            isInvalid={!!seasonFormErrors.endDate}
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: seasonFormErrors.endDate ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.endDate && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.endDate}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Location <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={seasonFormData.location}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (SEASON_LOCATION_INPUT_PATTERN.test(value)) {
                                setSeasonFormData({ ...seasonFormData, location: value });
                                if (seasonFormErrors.location) {
                                  setSeasonFormErrors({ ...seasonFormErrors, location: '' });
                                }
                              }
                            }}
                            isInvalid={!!seasonFormErrors.location}
                            placeholder="Enter location"
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: seasonFormErrors.location ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.location && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.location}
                            </Form.Control.Feedback>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <label className="form-label" style={{ 
                            fontSize: '14px', 
                            color: '#6c757d', 
                            fontWeight: '500',
                            marginBottom: '8px'
                          }}>
                            Season Year Range <span className="text-danger">*</span>
                          </label>
                          <Form.Control
                            type="text"
                            value={seasonFormData.seasonYearRange}
                            onChange={(e) => {
                              const value = e.target.value;
                              const yearRangePattern = /^[\d\-]*$/;
                              if (yearRangePattern.test(value) || value === '') {
                                setSeasonFormData({ ...seasonFormData, seasonYearRange: value });
                                if (seasonFormErrors.seasonYearRange) {
                                  setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: '' });
                                }
                              }
                            }}
                            onBlur={() => {
                              const trimmedRange = seasonFormData.seasonYearRange.trim();
                              if (trimmedRange) {
                                const yearRangePattern = /^(\d{4})(-\d{4})?$/;
                                if (!yearRangePattern.test(trimmedRange)) {
                                  setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'Season year range must be in format "2025" or "2025-2026"' });
                                } else if (trimmedRange.includes('-')) {
                                  const [startYear, endYear] = trimmedRange.split('-').map(Number);
                                  if (endYear < startYear) {
                                    setSeasonFormErrors({ ...seasonFormErrors, seasonYearRange: 'End year must be greater than or equal to start year' });
                                  }
                                }
                              }
                            }}
                            isInvalid={!!seasonFormErrors.seasonYearRange}
                            placeholder="e.g., 2025 or 2025-2026"
                            style={{ 
                              fontSize: '16px', 
                              fontWeight: '500', 
                              color: '#212529',
                              borderColor: seasonFormErrors.seasonYearRange ? '#dc3545' : undefined
                            }}
                          />
                          {seasonFormErrors.seasonYearRange && (
                            <Form.Control.Feedback type="invalid">
                              {seasonFormErrors.seasonYearRange}
                            </Form.Control.Feedback>
                          )}
                          <small className="text-muted">
                            Format: YYYY (e.g., 2025) or YYYY-YYYY (e.g., 2025-2026)
                          </small>
                        </div>
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md={12}>
                        <h6 className="mb-3">Media</h6>
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Season Vertical"
                          helperText={SEASON_VERTICAL_MEDIA_ENABLED ? 'Portrait artwork for Home and All Leagues. Recommended 2:3.' : 'Available after the portrait cutover.'}
                          value={seasonFormData.heroImage}
                          context={getSeasonMediaContext('portrait')}
                          previewAspectRatio="2 / 3"
                          disabled={seasonFormLoading || !SEASON_VERTICAL_MEDIA_ENABLED}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('heroImage', uploading)}
                          onChange={(value) => handleSeasonMediaChange('heroImage', value)}
                        />
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Season Horizontal - Web"
                          helperText="Landscape hero for public web. Falls back to App artwork when Web is empty, then Tournament Hero."
                          value={seasonFormData.detailHeroImage}
                          fallbackValue={seasonFormData.detailHeroImageApp}
                          fallbackLabel="Previewing App fallback"
                          context={getSeasonMediaContext('horizontal')}
                          previewAspectRatio="16 / 9"
                          disabled={seasonFormLoading}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('detailHeroImage', uploading)}
                          onChange={(value) => handleSeasonMediaChange('detailHeroImage', value)}
                        />
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Season Horizontal - App"
                          helperText="Landscape hero for native app. Falls back to Web artwork when App is empty, then Tournament Hero."
                          value={seasonFormData.detailHeroImageApp}
                          fallbackValue={seasonFormData.detailHeroImage}
                          fallbackLabel="Previewing Web fallback"
                          context={getSeasonMediaContext('horizontal-app')}
                          previewAspectRatio="16 / 9"
                          disabled={seasonFormLoading}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('detailHeroImageApp', uploading)}
                          onChange={(value) => handleSeasonMediaChange('detailHeroImageApp', value)}
                        />
                      </Col>
                      <Col md={4}>
                        <SeasonHeroImageUpload
                          label="Default Match Background"
                          helperText="Shown behind team logos for matches under this season. Falls back to the overall default when empty."
                          value={seasonFormData.matchCardBackgroundImage}
                          context={getSeasonMediaContext('match-card-background')}
                          previewAspectRatio="16 / 9"
                          disabled={seasonFormLoading}
                          onUploadingChange={(uploading) => handleSeasonMediaUploadingChange('matchCardBackgroundImage', uploading)}
                          onChange={(value) => handleSeasonMediaChange('matchCardBackgroundImage', value)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Row className="gy-4">
                          <Col md={12}><h6 className="mb-3">TV & Display Settings</h6></Col>
                          <Col md={12}><Row><Col md={4}><Form.Check type="switch" id="new-season-is-featured-on-tv" label="Featured on TV" checked={seasonFormData.isFeaturedOnTv} onChange={(event) => setSeasonFormData({ ...seasonFormData, isFeaturedOnTv: event.target.checked })} /></Col><Col md={4}><Form.Check type="switch" id="new-season-show-on-home-page" label="Show on Home Page" checked={seasonFormData.showOnHomePage} onChange={(event) => setSeasonFormData({ ...seasonFormData, showOnHomePage: event.target.checked })} /></Col></Row></Col>
                          <Col md={12}><Row><Col md={4}><Form.Label>TV Access Type</Form.Label><Form.Select value={seasonFormData.tvAccessType} onChange={(event) => setSeasonFormData({ ...seasonFormData, tvAccessType: event.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col><Col md={4}><Form.Label>Recording Access Type</Form.Label><Form.Select value={seasonFormData.recordingAccessType} onChange={(event) => setSeasonFormData({ ...seasonFormData, recordingAccessType: event.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col><Col md={4}><Form.Label>Highlight Access Type</Form.Label><Form.Select value={seasonFormData.highlightAccessType} onChange={(event) => setSeasonFormData({ ...seasonFormData, highlightAccessType: event.target.value })}><option value="free">Free</option><option value="paid">Paid</option></Form.Select></Col></Row></Col>
                          {seasonFormData.tvAccessType === 'paid' && (
                          <Col md={12}><Row>{seasonFormData.tvAccessType === 'paid' && <Col md={4}><Form.Check type="switch" id="new-season-match-tv-pass" label="Enable Match TV Pass" checked={seasonFormData.matchPassEnabled} onChange={(event) => setSeasonFormData({ ...seasonFormData, matchPassEnabled: event.target.checked })} />{seasonFormData.matchPassEnabled && <><Form.Label className="mt-2">Match override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.matchPassPrice} onChange={(event) => setSeasonFormData({ ...seasonFormData, matchPassPrice: event.target.value })} placeholder="Optional" /></>}</Col>}<Col md={4}><Form.Check type="switch" id="new-season-season-tv-pass" label="Enable Season TV Pass" checked={seasonFormData.seasonPassEnabled} onChange={(event) => setSeasonFormData({ ...seasonFormData, seasonPassEnabled: event.target.checked })} />{seasonFormData.seasonPassEnabled && <><Form.Label className="mt-2">Season override price (INR)</Form.Label><Form.Control type="number" min="0" step="0.01" value={seasonFormData.seasonPassPrice} onChange={(event) => setSeasonFormData({ ...seasonFormData, seasonPassPrice: event.target.value })} placeholder="Optional" /></>}</Col><Col md={4}><Form.Check type="switch" id="new-season-tournament-tv-pass" label="Enable Tournament TV Pass" checked={seasonFormData.tournamentPassEnabled} onChange={(event) => setSeasonFormData({ ...seasonFormData, tournamentPassEnabled: event.target.checked })} /></Col></Row></Col>
                          )}
                          <Col md={12}><Row><Col md={4}><Form.Label>Sort Order</Form.Label><Form.Control type="number" min="1" value={seasonFormData.sortOrder} onChange={(event) => setSeasonFormData({ ...seasonFormData, sortOrder: event.target.value })} /></Col><Col md={4}><Form.Label>TV Preview Duration (secs)</Form.Label><Form.Control type="number" min="0" value={seasonFormData.tvPreviewDuration} onChange={(event) => setSeasonFormData({ ...seasonFormData, tvPreviewDuration: event.target.value })} /></Col></Row></Col>
                        </Row>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2">
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={handleCancelSeasonEdit}
                        disabled={seasonFormLoading}
                        style={{ borderRadius: '6px' }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm" 
                        type="submit"
                        disabled={seasonFormLoading || isSeasonMediaUploading}
                        style={{ borderRadius: '6px' }}
                      >
                        {seasonFormLoading ? 'Saving...' : (editingSeason ? 'Update Season' : 'Add Season')}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            ) : null}

            {/* Seasons Table */}
            {seasonsLoading ? (
              <ShimmerLoader type="table" />
            ) : seasons.length === 0 ? (
              /* Only show message if tournament doesn't have DEFAULT season */
              !hasDefaultSeason && (
                <Card>
                  <Card.Body>
                    <div className="text-center py-4">
                      <p className="text-muted mb-0">No seasons added yet. Click "Add New Season" to create one.</p>
                    </div>
                  </Card.Body>
                </Card>
              )
            ) : (
              <Card>
                <Card.Body style={{ padding: '0' }}>
                  <Table responsive hover style={{ marginBottom: '0' }}>
                    <thead>
                      <tr>
                        <th>Season</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Age Category</th>
                        <th>City</th>
                        <th>Status</th>
                        <th>Reason</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSeasons.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4">
                            {seasons.length === 0 ? 'No seasons found for this tournament.' : 'No seasons match the current filters.'}
                          </td>
                        </tr>
                      ) : (
                        filteredSeasons.map((season) => (
                          <tr key={season._id}>
                            <td>{season.seasonLabel || season.seasonType || '-'}</td>
                            <td>{formatDisplayDate(season.startDate)}</td>
                            <td>{formatDisplayDate(season.endDate)}</td>
                            <td>
                              {season.ageCategories && season.ageCategories.length > 0 ? (
                                season.ageCategories.join(', ')
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{season.location || '-'}</td>
                            <td>{getStatusBadge(season.status)}</td>
                            <td>{season.cancellationInfo?.reason || '-'}</td>
                            <td style={{ textAlign: 'center' }}>
                              <div className="tournament-actions" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                <button
                                  className="action-btn btn-edit"
                                  onClick={() => handleEditSeason(season)}
                                  title="Edit Season"
                                >
                                  <i className="ri-pencil-line"></i>
                                </button>
                                <button
                                  className="action-btn btn-delete"
                                  onClick={() => handleDeleteSeason(season)}
                                  disabled={!canDeleteSeason(season)}
                                  title={canDeleteSeason(season) ? "Delete Season" : "Cannot delete ongoing or completed seasons"}
                                  style={{
                                    opacity: canDeleteSeason(season) ? 1 : 0.5,
                                    cursor: canDeleteSeason(season) ? 'pointer' : 'not-allowed'
                                  }}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </div>
        )}

      </Container>

      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        deleteModalFunction={confirmDeleteSeason}
        title="Delete Season"
        message={deleteSeasonTarget ? `Are you sure you want to delete "${deleteSeasonTarget.seasonLabel}"? This action cannot be undone.` : ''}
        confirmLabel="Delete Season"
        cancelLabel="Cancel"
        isLoading={isDeletingSeason}
        error={deleteSeasonError}
      />
    </div>
  );
};

export default SeasonsListingPage;
